import { Injectable } from '@angular/core';
import { Availability, ShopsAvailable } from '@availability/const';
import { CustomActiveCartService } from '@cart/data-access';
import { Store, select } from '@ngrx/store';
import { SelectedPrice } from '@product/const';
import { SelectedPriceService } from '@product/data-access';
import { convertFromSaleUnit } from '@product/utils';
import { StateUtils } from '@spartacus/core';
import { Observable, debounceTime } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AvailabilityActions } from '../store/actions';
import { StateWithAvailability } from '../store/availability-state';
import { AvailabilitySelectors, ShopsAvailableSelectors } from '../store/selectors';

@Injectable({
  providedIn: 'root',
})
export class ProductAvailabilityFacade {
  constructor(
    private store: Store<StateWithAvailability>,
    private selectedPriceService: SelectedPriceService,
    private customActiveCartService: CustomActiveCartService,
  ) {}

  loadAvailability(productCode: string, unitCode: string, quantity: number): void {
    this.store.dispatch(new AvailabilityActions.LoadAvailability(productCode, unitCode, quantity));
  }

  getAvailability(productCode: string): Observable<StateUtils.LoaderState<Availability>> {
    return this.getUnitCodeQuantity(productCode).pipe(
      debounceTime(500),
      switchMap(({ unitCode, quantity }) => {
        return this.store.pipe(
          select(
            AvailabilitySelectors.getAvailability({
              productCode,
              unitCode,
            }),
          ),
          tap(({ loading, error, success, value }: StateUtils.LoaderState<Availability>) => {
            if (!loading && !error && (!success || value.quantity !== quantity)) {
              this.loadAvailability(productCode, unitCode, quantity);
            }
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getAvailabilityLoading(productCode: string): Observable<boolean | undefined> {
    return this.getUnitCodeQuantity(productCode).pipe(
      mergeMap(({ unitCode }) =>
        this.store.pipe(
          select(
            AvailabilitySelectors.getAvailabilityLoading({
              productCode,
              unitCode,
            }),
          ),
        ),
      ),
    );
  }

  getAvailabilityError(productCode: string): Observable<boolean | undefined> {
    return this.getUnitCodeQuantity(productCode).pipe(
      mergeMap(({ unitCode }) =>
        this.store.pipe(
          select(
            AvailabilitySelectors.getAvailabilityError({
              productCode,
              unitCode,
            }),
          ),
        ),
      ),
    );
  }

  loadShopsAvailable(productCode: string, unitCode: string): void {
    this.store.dispatch(new AvailabilityActions.LoadShopsAvailable(productCode, unitCode));
  }

  getShopsAvailable(productCode: string, unitCode: string): Observable<StateUtils.LoaderState<ShopsAvailable>> {
    return this.store.pipe(
      select(ShopsAvailableSelectors.getShopsAvailable(productCode, unitCode)),
      tap((state?: StateUtils.LoaderState<ShopsAvailable>) => {
        if (!state.value && !state.loading && !state.error && !state.success) {
          this.loadShopsAvailable(productCode, unitCode);
        }
      }),
    );
  }

  getShopsAvailableLoading(productCode: string, unitCode: string): Observable<boolean | undefined> {
    return this.store.pipe(select(ShopsAvailableSelectors.getShopsAvailableLoading(productCode, unitCode)));
  }

  getShopsAvailableError(productCode: string, unitCode: string): Observable<boolean | undefined> {
    return this.store.pipe(select(ShopsAvailableSelectors.getShopsAvailableError(productCode, unitCode)));
  }

  getShopsAvailableSuccess(productCode: string, unitCode: string): Observable<boolean | undefined> {
    return this.store.pipe(select(ShopsAvailableSelectors.getShopsAvailableSuccess(productCode, unitCode)));
  }

  private getUnitCodeQuantity(productCode: string): Observable<{ unitCode: string; quantity: number }> {
    return this.selectedPriceService.getSelectedPrice(productCode).pipe(
      switchMap((selectedPrice: SelectedPrice) =>
        this.customActiveCartService.getEntry(productCode).pipe(
          map((entry) => entry?.quantity),
          map((quantity) => (quantity !== undefined ? convertFromSaleUnit(selectedPrice, quantity).amount : 1)),
          map((quantity) => ({ quantity, unitCode: selectedPrice.unit?.code })),
        ),
      ),
      distinctUntilChanged((a, b) => a.quantity === b.quantity && a.unitCode === b.unitCode),
    );
  }
}
